import mustache from "mustache";
import { BaseCreateRender } from "../../../base/BaseCreateRender";
import template from "./sateliteCapaMenuModalTemplate.html";

mustache.parse(template);

/** Opción del menú de capa (mismo par value/label que las `<option>` del
 *  select real). */
export type CapaOpcion = { value: string; label: string };

/** Modal chico (auto-montado sobre `body`) con la lista de capas disponibles,
 *  estilo menú de app móvil — para pantallas angostas donde el `<select>`
 *  nativo se oculta. Un solo botón "Cerrar" en el footer; elegir una opción
 *  notifica y cierra de una. */
export class SateliteCapaMenuModal extends BaseCreateRender {
  /** Id del elemento raíz auto-montado en `body`. */
  protected readonly elementId = "sateliteCapaMenuModal";

  /**
   * @param _opciones Opciones a listar (mismas que el `<select>` real).
   * @param _valorActual Value actualmente seleccionado (se resalta en la lista).
   * @param _onPick Notifica el value elegido; el modal se cierra solo después.
   */
  constructor(
    private readonly _opciones: CapaOpcion[],
    private readonly _valorActual: string,
    private readonly _onPick: (value: string) => void,
  ) {
    super();
  }

  /** HTML del modal: overlay + card con la lista de opciones. */
  protected getHtml(): string {
    return mustache.render(template, {
      opciones: this._opciones.map((opcion) => ({
        ...opcion,
        activa: opcion.value === this._valorActual,
      })),
    });
  }

  /** Captura overlay/lista/botón y cablea eventos. */
  protected afterInit(element: HTMLDivElement): void {
    this._overlay = element.querySelector<HTMLDivElement>(".sx-modal-overlay");
    this._lista = element.querySelector<HTMLDivElement>(".scm-lista");
    this._backBtn = element.querySelector<HTMLButtonElement>(
      '[data-act="back"]',
    );

    this._overlay?.addEventListener("click", this._onOverlayClick);
    this._backBtn?.addEventListener("click", this._onBack);
    this._lista?.addEventListener("click", this._onOptionClick);

    requestAnimationFrame(() =>
      this._overlay?.classList.add("sx-modal-overlay--open"),
    );
  }

  /** Libera los listeners antes de desmontar el modal. */
  protected beforeDispose(): void {
    this._overlay?.removeEventListener("click", this._onOverlayClick);
    this._backBtn?.removeEventListener("click", this._onBack);
    this._lista?.removeEventListener("click", this._onOptionClick);
  }

  private _overlay: HTMLDivElement | null = null;
  private _lista: HTMLDivElement | null = null;
  private _backBtn: HTMLButtonElement | null = null;

  /** Cierra el modal solo si el click fue en el backdrop (no en el contenido). */
  private _onOverlayClick = (e: MouseEvent): void => {
    if (e.target === this._overlay) this.dispose();
  };

  private _onBack = (): void => this.dispose();

  /** Delegado del click en las opciones: notifica el value elegido y cierra. */
  private _onOptionClick = (e: Event): void => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(
      "[data-value]",
    );
    if (!btn?.dataset.value) return;
    this._onPick(btn.dataset.value);
    this.dispose();
  };
}
